import { track } from "@/features/analytics";
import { useSearchGifsMutation } from "@api/media";
import debounce from "lodash.debounce";
import { useCallback, useEffect, useRef, useState } from "react";
import { GIF_CATEGORIES, GIF_QUERY_KEY } from "../constants";
import { useGifsStoreShallow } from "../store";

const PER_PAGE = 30;

interface IProps {
  searchQuery: string;
}

interface IFetchProps {
  replace?: boolean;
  signal?: AbortSignal;
  nextPage?: boolean;
  query?: string;
}

export const useFetchGifs = ({ searchQuery }: IProps) => {
  const { category, columnsData, setColumnsData } = useGifsStoreShallow(
    (s) => ({
      columnsData: s.columnsData,
      setColumnsData: s.setColumnsData,
      category: s.category,
    }),
  );

  const isInit = useRef(true);
  const [isReplacingGifs, setIsReplacingGifs] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isError, setIsError] = useState(false);
  const [reloadKey, setReloadKey] = useState({});
  const latestPage = useRef(0);
  const lastSystemQuery = useRef("");

  const [fetchGifs] = useSearchGifsMutation({
    fixedCacheKey: GIF_QUERY_KEY,
  });

  const fetchPageOfGifs = async ({
    query = searchQuery || lastSystemQuery.current,
    signal,
    replace,
    nextPage,
  }: IFetchProps) => {
    try {
      setIsError(false);
      setIsFetchingMore(true);

      if (replace) {
        setIsReplacingGifs(true);
        latestPage.current = 0;
      } else if (nextPage) {
        setIsFetchingMore(true);
        latestPage.current++;
      }

      const data = await fetchGifs({
        query: query,
        offset: latestPage.current * PER_PAGE,
        limit: PER_PAGE,
        bundle: "clips_grid_picker",
        signal,
      }).unwrap();

      setColumnsData({
        gifs: data.data,
        extend: nextPage,
      });

      setIsReplacingGifs(false);
      setIsFetchingMore(false);
    } catch (e) {
      if (!signal?.aborted) {
        track("error fetching unsplash gifs", {
          error: e,
          query: query,
        });
        setIsError(true);
        setIsReplacingGifs(false);
        setIsFetchingMore(false);
      }
    }
  };

  const replaceGifsData = async (controller: AbortController) => {
    setIsError(false);
    setIsReplacingGifs(true);

    let query = searchQuery;
    if (query.length === 0) {
      const found = GIF_CATEGORIES.find((c) => c.key === category);
      if (found) {
        query = found.query;
      }
    }

    lastSystemQuery.current = query;
    await fetchPageOfGifs({
      query,
      replace: true,
      signal: controller.signal,
    });

    if (!controller.signal.aborted) {
      setIsReplacingGifs(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();

    if (isInit.current) {
      isInit.current = false;
      if (columnsData[0].length === 0) {
        replaceGifsData(controller);
      } else {
        setIsReplacingGifs(false);
      }
      return;
    }

    replaceGifsData(controller);

    return () => {
      controller.abort();
    };
  }, [searchQuery, category, reloadKey]);

  const canLoadMoreGifs = !isFetchingMore && !isReplacingGifs;

  const fetchMoreGifs = useCallback(
    debounce(() => {
      if (
        !canLoadMoreGifs ||
        columnsData.reduce((sum, col) => sum + col.length, 0) < PER_PAGE / 2
      ) {
        return;
      }
      fetchPageOfGifs({ nextPage: true });
    }, 100),
    [canLoadMoreGifs, columnsData, fetchPageOfGifs],
  );

  return {
    isError,
    reloadData: () => setReloadKey({}),
    fetchMoreGifs,
    shouldShowSkeleton: isReplacingGifs,
    canLoadMoreGifs,
  };
};
